import { useTranslation } from 'react-i18next';
import { useUrlPage } from '../../hooks/attendance/urlPage';
import { useSuspensePaginatedRedVelvetQuery } from '../../hooks/paginatedRedVelvetQuery';
import { Alert, Box, Button, Header, SpaceBetween } from '@amzn/awsui-components-react';
import { AccessibleSuspenseLoading } from '../accessibility/accessibleSuspenseLoading';
import { EventDetailsTable } from '../attendance/coordinator/coordinatorEventOwner';
import { useInjectEventDetailsForFirstPageOfQueryData } from '../../hooks/events';
import { DEFAULT_COORDINATOR_SHIFT_PAGE_SIZE } from './coordinatorEventShiftsTablePaginationPreference';
import { externalLinks } from '../../data/externalLinks';
import { UrlControlledPagination } from '../../hooks/urlControlledPagination';
import { COORDINATOR_EVENT_SHIFTS_LIST } from '../../utils/constants';

export const DEFAULT_EVENT_PAGE_SIZE = 10;
export type CoordinatorEventShiftsListProps = {
  startDate?: Date;
  endDate?: Date;
  sort: 'newest-first' | 'oldest-first';
  isPastTab?: boolean;
};

export function SuspenseCoordinatorEventShiftsList({
  startDate,
  endDate,
  sort,
  isPastTab = false,
}: CoordinatorEventShiftsListProps) {
  const { t } = useTranslation(['activity', 'translation']);
  const { page } = useUrlPage(1);
  const injectEventDetailsInReactCache = useInjectEventDetailsForFirstPageOfQueryData();
  // GOLDEN PATH NOTE
  // We are using the suspense query here for accessibility, since it will make the
  // accessible loading in the parent work correctly
  const { queryResult } = useSuspensePaginatedRedVelvetQuery(
    {
      redVelvetQueryKey: [
        'getEventDetailsManagedByUser',
        {
          descending: sort === 'newest-first',
          startDateTime: startDate ? `${startDate.toISOString()}` : undefined,
          endDateTime: endDate ? `${endDate.toISOString()}` : undefined,
          shiftPageSize: DEFAULT_COORDINATOR_SHIFT_PAGE_SIZE,
          pageSize: DEFAULT_EVENT_PAGE_SIZE,
        },
      ],
    },
    page
  );

  if (!queryResult.data) {
    return (
      <Alert
        type="error"
        i18nStrings={{
          errorIconAriaLabel: t('coordinatorEventShiftsList.errorAria'),
          dismissAriaLabel: t('alert.dismiss.error'),
        }}
      >
        {t('coordinatorEventShiftsList.beyondPageIndex', { page })}
      </Alert>
    );
  }

  if (queryResult.error) {
    return (
      <Alert
        type="error"
        i18nStrings={{
          errorIconAriaLabel: t('coordinatorEventShiftsList.errorAria'),
          dismissAriaLabel: t('alert.dismiss.error'),
        }}
      >{`${queryResult.error}`}</Alert>
    );
  }

  if (!queryResult.data?.eventDetails || queryResult.data?.eventDetails.length === 0) {
    return (
      <Box textAlign="center">
        <SpaceBetween direction="vertical" size="s">
          <b className="pseudoHeader">{t('coordinatorEventShiftsList.noActivities')}</b>
          <div>
            <div>{t('coordinatorEventShiftsList.noActivitiesSubtitle1')}</div>
            <div>{t('coordinatorEventShiftsList.noActivitiesSubtitle2')}</div>
            <div>{t('coordinatorEventShiftsList.noActivitiesSubtitle3')}</div>
          </div>

          <Button
            variant="primary"
            data-aci-analytics-name={COORDINATOR_EVENT_SHIFTS_LIST + '_planEventLink'}
            target="_blank"
            href={externalLinks.support.planningPlaybooks}
          >
            {t('coordinatorEventShiftsList.planAnEvent')}
          </Button>
        </SpaceBetween>
      </Box>
    );
  }

  const eventDetails = queryResult.data?.eventDetails ?? [];
  injectEventDetailsInReactCache(eventDetails);
  return (
    <div>
      <SpaceBetween direction="vertical" size="s">
        {eventDetails.map((eventDetail, index) => (
          <EventDetailsTable
            key={eventDetail.event.eventId}
            eventId={eventDetail.event.eventId}
            fromEventDetailPage={false}
            isPastTab={isPastTab}
          />
        ))}
      </SpaceBetween>
    </div>
  );
}
export function CoordinatorEventShiftsList({
  startDate,
  endDate,
  sort,
  isPastTab = false,
}: CoordinatorEventShiftsListProps) {
  const namespaces = ['activity', 'translation'];
  const { t } = useTranslation(namespaces);
  const getEventDetailsManagedByUserAPIInput = {
    pageSize: DEFAULT_EVENT_PAGE_SIZE,
    descending: sort === 'newest-first',
    startDateTime: startDate ? `${startDate.toISOString()}` : undefined,
    endDateTime: endDate ? `${endDate.toISOString()}` : undefined,
    shiftPageSize: DEFAULT_COORDINATOR_SHIFT_PAGE_SIZE,
  };
  const paginationProps = {
    paginationPrefix: 'coordinatorEventShiftsList.pagination',
    namespaces: namespaces,
    tableName: t('coordinatorEventShiftsList.tableName'),
  };
  return (
    <SpaceBetween direction="vertical" size="s">
      {/* GOLDEN PATH NOTE
            For accessibility reasons, we always want to show the pagination, even if there is no data.
            This allows users to rapidly paginate even while the content of another page is loading */}
      <Header
        actions={
          <UrlControlledPagination
            redVelvetQueryKey={[
              'getEventDetailsManagedByUser',
              getEventDetailsManagedByUserAPIInput,
            ]}
            {...paginationProps}
          />
        }
      />
      {/* GOLDEN PATH NOTE
                Since we are actually displaying loaded data here, we want to make sure it is accessible
                We need to use AccessibleSuspenseLoading RIGHT before the component that provides a suspense
                query to make sure the suspense contract is upheld.  We also name the child with the Suspense
                name to indicate the presence of the contract */}
      <AccessibleSuspenseLoading loadingText={t('coordinatorEventShiftsList.loading')}>
        <SuspenseCoordinatorEventShiftsList
          startDate={startDate}
          endDate={endDate}
          sort={sort}
          isPastTab={isPastTab}
        />
      </AccessibleSuspenseLoading>
      <Header
        actions={
          <UrlControlledPagination
            redVelvetQueryKey={[
              'getEventDetailsManagedByUser',
              getEventDetailsManagedByUserAPIInput,
            ]}
            {...paginationProps}
          />
        }
      />
    </SpaceBetween>
  );
}
